/* Header 2026 — dropdowns, mobile menu, sticky behaviour. */
(() => {
  if (customElements.get('header-2026')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 250;
  const FOCUSABLE = 'a[href], button:not([disabled]), summary, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  class Header2026 extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.openHandle = null;
      this.openedBy = null;
      this.openTimer = null;
      this.closeTimer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.querySelectorAll('[data-hdr-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.togglePanel(trigger.dataset.hdrTrigger));
      });
      this.addEventListener('pointerover', (event) => this.onPointerOver(event));
      this.addEventListener('pointerleave', (event) => this.onPointerLeave(event));

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      this.desktop.addEventListener('change', this.onBreakpointChange);

      this.initMobileMenu();
      this.initSticky();
      this.initMedia();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.desktop.removeEventListener('change', this.onBreakpointChange);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.teardownMobileMenu();
      this.teardownSticky();
    }

    panel(handle) {
      return this.querySelector(`[data-hdr-panel="${CSS.escape(handle)}"]`);
    }

    trigger(handle) {
      return this.querySelector(`[data-hdr-trigger="${CSS.escape(handle)}"]`);
    }

    focusable(root) {
      return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    }

    // Hover is tracked for the whole header: the nav row, the gap strip under the bar and the open panel
    // all keep a panel open, so a slow move from a trigger down into its panel never closes it.
    onPointerOver(event) {
      if (event.pointerType !== 'mouse' || !this.desktop.matches) return;
      const trigger = event.target.closest('[data-hdr-trigger]');
      if (trigger) {
        this.hoverTrigger(trigger.dataset.hdrTrigger);
      } else if (event.target.closest('[data-hdr-panel], .hdr__nav, .hdr__menu')) {
        clearTimeout(this.closeTimer);
      } else {
        this.scheduleClose();
      }
    }

    onPointerLeave(event) {
      if (event.pointerType !== 'mouse' || !this.desktop.matches) return;
      this.scheduleClose();
    }

    hoverTrigger(handle) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      if (this.openHandle === handle) return;
      // Another panel is already open: swap instantly instead of waiting for intent again.
      if (this.openHandle) {
        this.openPanel(handle);
        return;
      }
      this.openTimer = setTimeout(() => this.openPanel(handle), OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.openHandle) this.closeTimer = setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }

    togglePanel(handle) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.openHandle === handle && this.openedBy === 'click') {
        this.closePanel();
      } else {
        this.openPanel(handle, 'click');
      }
    }

    openPanel(handle, openedBy = 'hover') {
      const panel = this.panel(handle);
      if (!panel) return;
      if (this.openHandle && this.openHandle !== handle) this.closePanel();
      this.style.setProperty('--hdr-panel-offset', `${Math.round(this.getBoundingClientRect().bottom) + 16}px`);
      panel.classList.add('is-open');
      this.trigger(handle)?.setAttribute('aria-expanded', 'true');
      this.openHandle = handle;
      this.openedBy = openedBy;
      this.classList.add('has-open-panel');
    }

    closePanel({ returnFocus = false } = {}) {
      clearTimeout(this.openTimer);
      if (!this.openHandle) return;
      const trigger = this.trigger(this.openHandle);
      this.panel(this.openHandle)?.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      this.openHandle = null;
      this.openedBy = null;
      this.classList.remove('has-open-panel');
      if (returnFocus) trigger?.focus();
    }

    onDocumentClick(event) {
      if (this.openHandle && !this.contains(event.target)) this.closePanel();
    }

    onKeydown(event) {
      // layout/theme.liquid adds a document-level Enter → e.target.click() handler; buttons already click
      // themselves on Enter, so let the native activation be the only one.
      if (event.key === 'Enter' && event.target.closest('button')) {
        event.stopPropagation();
        return;
      }
      if (event.key === 'Escape') {
        if (this.isMobileOpen()) {
          event.stopPropagation();
          this.closeMobileMenu();
        } else if (this.openHandle) {
          event.stopPropagation();
          this.closePanel({ returnFocus: true });
        }
        return;
      }
      if (event.key === 'Tab' && this.isMobileOpen()) this.onMenuTab(event);
      else if (event.key === 'Tab' && this.openHandle && this.desktop.matches) this.onPanelTab(event);
    }

    // Panels sit after the utilities in the DOM; route Tab so the order is trigger → panel → next nav item.
    onPanelTab(event) {
      const trigger = this.trigger(this.openHandle);
      const items = this.focusable(this.panel(this.openHandle));
      if (!trigger || !items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!event.shiftKey && active === trigger) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        trigger.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        const barItems = this.focusable(this.querySelector('.hdr__bar'));
        const next = barItems[barItems.indexOf(trigger) + 1];
        this.closePanel();
        next?.focus();
      }
    }

    // Focus trap for the mobile dialog. Dawn's trapFocus picks its first/last element without checking
    // visibility, and the menu holds desktop-only items, so wrap over the visible focusables instead.
    onMenuTab(event) {
      const items = this.focusable(this.menu);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!this.menu.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      }
    }

    onFocusOut(event) {
      if (!this.openHandle) return;
      const next = event.relatedTarget;
      if (next && !this.contains(next)) this.closePanel();
    }

    onBreakpointChange() {
      this.closePanel();
      this.closeMobileMenu({ returnFocus: false });
      this.syncMobileOrder();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const panel = event.target.closest('[data-hdr-panel]');
      if (!panel) return;
      if (this.desktop.matches) {
        this.openPanel(panel.dataset.hdrPanel, 'click');
      } else {
        this.openMobileMenu();
      }
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.closePanel();
    }

    // Filled in by later tasks.
    initMobileMenu() {
      this.menu = this.querySelector('[data-hdr-menu]');
      this.menuBody = this.querySelector('[data-hdr-menu-body]');
      this.tabsTrack = this.querySelector('[data-hdr-tabs-track]');
      this.menuToggle = this.querySelector('.hdr__bar [data-hdr-menu-toggle]');
      if (!this.menu || !this.menuBody || !this.menuToggle) return;

      this.querySelectorAll('[data-hdr-menu-toggle]').forEach((button) =>
        button.addEventListener('click', () => (this.isMobileOpen() ? this.closeMobileMenu() : this.openMobileMenu()))
      );
      this.querySelector('[data-hdr-menu-search]')?.addEventListener('click', () => {
        this.closeMobileMenu({ returnFocus: false });
        this.querySelector('details-modal.header__search summary')?.click();
      });
      this.menu.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link && !link.hasAttribute('data-hdr-tab')) this.closeMobileMenu({ returnFocus: false });
      });
      this.buildTabs();
      this.syncMobileOrder();
    }

    // CSS `order` only changes what is painted; screen readers and Tab follow the DOM (WCAG 1.3.2, 2.4.3).
    // On mobile, move groups and buttons into the menu body in their mobile order; restore them for desktop.
    syncMobileOrder() {
      if (!this.menuBody) return;
      if (this.desktop.matches) {
        this.restoreDesktopOrder();
      } else {
        this.applyMobileOrder();
      }
    }

    applyMobileOrder() {
      if (this.orderMarkers) return;
      const items = Array.from(
        this.menuBody.querySelectorAll('.hdr-panel__inner > .hdr-group, .hdr-panel__inner > .hdr-button')
      );
      this.orderMarkers = items.map((el) => {
        const marker = document.createComment('hdr-slot');
        el.before(marker);
        return { el, marker };
      });
      items
        .map((el, index) => ({ el, index, order: Number(el.style.getPropertyValue('--hdr-mobile-order')) || 10 }))
        .sort((a, b) => a.order - b.order || a.index - b.index)
        .forEach(({ el }) => this.menuBody.append(el));
    }

    restoreDesktopOrder() {
      if (!this.orderMarkers) return;
      this.orderMarkers.forEach(({ el, marker }) => marker.replaceWith(el));
      this.orderMarkers = null;
    }

    teardownMobileMenu() {
      if (this.onMenuScroll) this.menuBody?.removeEventListener('scroll', this.onMenuScroll);
      if (this.isMobileOpen()) document.body.classList.remove('overflow-hidden');
    }

    isMobileOpen() {
      return this.classList.contains('is-mobile-open');
    }

    // Groups that appear as tabs, in mobile order (inline --hdr-mobile-order, then DOM order).
    mobileGroups() {
      return Array.from(this.querySelectorAll('[data-hdr-group][data-hdr-tab-label]'))
        .filter((group) => group.querySelector('.hdr-group__items')?.children.length > 0)
        .map((group, index) => ({ group, index, order: Number(group.style.getPropertyValue('--hdr-mobile-order')) || 10 }))
        .sort((a, b) => a.order - b.order || a.index - b.index)
        .map(({ group }) => group);
    }

    buildTabs() {
      if (!this.tabsTrack) return;
      this.tabsTrack.querySelectorAll('[data-hdr-tab]').forEach((tab) => tab.remove());
      const extraLink = this.tabsTrack.querySelector('.hdr__tab--link');
      const iconTemplate = this.querySelector('template[data-hdr-tag-icon]');

      this.mobileGroups().forEach((group) => {
        const tab = document.createElement('a');
        tab.className = 'hdr__tab';
        tab.href = `#${group.id}`;
        tab.dataset.hdrTab = group.id;
        if (group.dataset.hdrTabIcon === 'tag' && iconTemplate) {
          const icon = document.createElement('span');
          icon.className = 'hdr__tab-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.append(iconTemplate.content.cloneNode(true));
          tab.append(icon);
        }
        tab.append(document.createTextNode(group.dataset.hdrTabLabel));
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollToGroup(group);
        });
        this.tabsTrack.insertBefore(tab, extraLink);
      });
    }

    scrollToGroup(group) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setActiveTab(group.id);
      this.suppressSpyUntil = Date.now() + 800;
      this.menuBody.scrollTo({ top: group.offsetTop - 24, behavior: reduce ? 'auto' : 'smooth' });
    }

    setActiveTab(groupId) {
      if (!this.tabsTrack) return;
      this.tabsTrack.querySelectorAll('[data-hdr-tab]').forEach((tab) => {
        const active = tab.dataset.hdrTab === groupId;
        if (active) {
          tab.setAttribute('aria-current', 'true');
          tab.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    }

    // Scroll-spy: the current tab is the last group whose top has passed the upper third of the menu body,
    // or the last group once the body is scrolled to the end (short final sections never reach that line).
    observeGroups() {
      this.menuBody.removeEventListener('scroll', this.onMenuScroll);
      this.onMenuScroll = this.onMenuScroll || (() => {
        if (this.spyFrame) return;
        this.spyFrame = requestAnimationFrame(() => {
          this.spyFrame = null;
          this.updateActiveTab();
        });
      });
      this.menuBody.addEventListener('scroll', this.onMenuScroll, { passive: true });
    }

    updateActiveTab() {
      if (this.suppressSpyUntil && Date.now() < this.suppressSpyUntil) return;
      const groups = this.mobileGroups();
      if (!groups.length) return;
      const body = this.menuBody;
      const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2;
      const line = body.getBoundingClientRect().top + body.clientHeight / 3;
      let current = groups[0];
      groups.forEach((group) => {
        if (group.getBoundingClientRect().top <= line) current = group;
      });
      this.setActiveTab((atEnd ? groups[groups.length - 1] : current).id);
    }

    openMobileMenu() {
      if (!this.menu || this.desktop.matches || this.isMobileOpen()) return;
      this.closePanel();
      this.classList.add('is-mobile-open');
      this.menu.setAttribute('role', 'dialog');
      this.menu.setAttribute('aria-modal', 'true');
      this.menu.setAttribute('aria-label', this.menu.dataset.hdrMenuLabel || 'Menu');
      this.menuToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      this.menuBody.scrollTop = 0;
      const first = this.mobileGroups()[0];
      if (first) this.setActiveTab(first.id);
      this.observeGroups();
      this.menu.querySelector('[data-hdr-menu-close]')?.focus();
    }

    closeMobileMenu({ returnFocus = true } = {}) {
      if (!this.isMobileOpen()) return;
      this.classList.remove('is-mobile-open');
      this.menu.removeAttribute('role');
      this.menu.removeAttribute('aria-modal');
      this.menu.removeAttribute('aria-label');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      if (this.onMenuScroll) this.menuBody.removeEventListener('scroll', this.onMenuScroll);
      if (returnFocus) this.menuToggle.focus();
    }
    // Ported from the existing header's StickyHeader (sections/header.liquid) so behaviour is unchanged.
    initSticky() {
      this.sectionEl = this.closest('.section-header');
      if (!this.sectionEl) return;
      this.setHeaderHeight = () =>
        document.documentElement.style.setProperty('--header-height', `${this.sectionEl.offsetHeight}px`);
      this.setHeaderHeight();
      this.resizeObserver = new ResizeObserver(this.setHeaderHeight);
      this.resizeObserver.observe(this.sectionEl);

      const type = this.dataset.stickyType;
      if (type === 'none') return;
      this.alwaysSticky = type === 'always' || type === 'reduce-logo-size';
      if (this.alwaysSticky) this.sectionEl.classList.add('shopify-section-header-sticky');

      const rect = this.sectionEl.getBoundingClientRect();
      this.headerTop = rect.top + window.scrollY;
      this.headerBottom = rect.bottom + window.scrollY;
      this.lastScrollTop = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    teardownSticky() {
      this.resizeObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const scrollTop = window.scrollY;
      const past = scrollTop > this.headerBottom;
      this.sectionEl.classList.toggle('scrolled-past-header', past);

      if (!this.alwaysSticky) {
        if (past && scrollTop > this.lastScrollTop) {
          this.closePanel();
          this.sectionEl.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        } else if (past && scrollTop < this.lastScrollTop) {
          this.sectionEl.classList.add('shopify-section-header-sticky', 'animate');
          this.sectionEl.classList.remove('shopify-section-header-hidden');
        } else if (scrollTop <= this.headerTop) {
          this.sectionEl.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        }
      }
      this.lastScrollTop = scrollTop;
    }
    initMedia() {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.pause();
      });
    }
  }

  customElements.define('header-2026', Header2026);
})();
